import React, { useState } from "react";
import styled from "styled-components";
import { Modal, Tag, Descriptions, Button, Alert, Collapse, message } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  CheckOutlined,
  CodeOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { KpiBatchInfoItem } from "../../types/kpiBatchInfo";
import {
  formatBatchDate,
  formatRangeLabel,
  checkBatchFreshness,
} from "../../utils/kpiBatchUtils";

const ErrorPre = styled.pre`
  background: #1e1e1e;
  color: #f87171;
  padding: 12px;
  border-radius: 6px;
  font-size: 12px;
  max-height: 250px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-all;
  margin: 0;
`;

const JsonPre = styled.pre`
  background: #f8fafc;
  color: #334155;
  padding: 10px;
  border-radius: 6px;
  font-size: 11px;
  max-height: 200px;
  overflow-y: auto;
  margin: 0;
`;

type Props = {
  item: KpiBatchInfoItem | null;
  open: boolean;
  referenceDate?: string | Date;
  onClose: () => void;
};

const AdminBatchDetailModal: React.FC<Props> = ({
  item,
  open,
  referenceDate,
  onClose,
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  if (!item) return null;

  const isSuccess = item.status === "SUCCESS";
  const runAtInfo = formatBatchDate(item.runAt);
  const rangeLabel = formatRangeLabel(item.range);
  const freshness = checkBatchFreshness(item.runAt, item.range, referenceDate);

  const handleCopyError = async () => {
    if (!item.error) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(item.error);
      }
      setCopied(true);
      message.success(t("admin.copied", "Copié dans le presse-papier !"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      message.error("Erreur lors de la copie");
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <span style={{ fontSize: "16px", fontWeight: 700 }}>{item.kpiName}</span>
          <Tag color="geekblue">{item.kpiCategory}</Tag>
          <Tag
            color={isSuccess ? "success" : "error"}
            icon={isSuccess ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            style={{ fontWeight: 600 }}
          >
            {item.status}
          </Tag>
          {freshness.isWarning && (
            <Tag color="warning" icon={<WarningOutlined />} style={{ fontWeight: 600 }}>
              {freshness.shortMessage}
            </Tag>
          )}
        </div>
      }
      width={720}
      footer={[
        <Button key="close" type="primary" onClick={onClose}>
          {t("admin.modal.close", "Fermer")}
        </Button>,
      ]}
    >
      <div style={{ marginTop: "16px" }}>
        <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label={t("admin.table.id", "ID")}>
            <strong>#{item.id}</strong>
          </Descriptions.Item>
          <Descriptions.Item label={t("admin.table.country", "Pays")}>
            <Tag color="blue">{item.countryKey}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t("admin.table.category", "Catégorie")}>
            <strong>{item.kpiCategory}</strong>
          </Descriptions.Item>
          <Descriptions.Item label={t("admin.table.kpi", "KPI")}>
            {item.kpiName}
          </Descriptions.Item>
          <Descriptions.Item label={t("admin.table.range", "Temporalité")}>
            <span>
              {rangeLabel} <span style={{ color: "#9ca3af", fontSize: "11px" }}>({item.range})</span>
            </span>
          </Descriptions.Item>
          <Descriptions.Item label={t("admin.table.status", "Statut")}>
            <Tag color={isSuccess ? "success" : "error"}>{item.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t("admin.table.run_at", "Date d'exécution")} span={2}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span>
                <strong>{runAtInfo.formatted}</strong>{" "}
                <span style={{ color: "#6b7280" }}>({runAtInfo.relative})</span>
              </span>
              {freshness.isWarning ? (
                <Tag color="warning" icon={<WarningOutlined />} data-testid="modal-warning-tag">
                  {freshness.shortMessage}
                </Tag>
              ) : (
                <Tag color="success" icon={<CheckCircleOutlined />}>
                  {t("admin.up_to_date", "À jour")}
                </Tag>
              )}
            </div>
          </Descriptions.Item>
        </Descriptions>

        {/* Freshness Warning Banner */}
        {freshness.isWarning && (
          <Alert
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            message={t("admin.modal.warning_title", "Attention : Retard de synchronisation détecté")}
            description={
              <div>
                <div>
                  {freshness.message} (période attendue : <strong>{freshness.periodLabel}</strong>).
                </div>
                <div style={{ fontSize: "12px", marginTop: 4, color: "#4b5563" }}>
                  Pour la temporalité <strong>{rangeLabel}</strong> ({item.range}), la dernière exécution doit
                  être effectuée dans la même période.
                </div>
              </div>
            }
            style={{ marginTop: "14px" }}
            data-testid="modal-freshness-warning"
          />
        )}

        {/* Error section */}
        <div style={{ marginTop: "16px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "8px",
            }}
          >
            <span style={{ fontWeight: 600, fontSize: "13px" }}>
              {t("admin.modal.error_title", "Détail de l'erreur / Message")} :
            </span>
            {item.error && (
              <Button
                size="small"
                icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                onClick={handleCopyError}
              >
                {copied ? t("admin.copied", "Copié !") : t("admin.copy", "Copier l'erreur")}
              </Button>
            )}
          </div>

          {item.error ? (
            <ErrorPre data-testid="modal-error-content">{item.error}</ErrorPre>
          ) : (
            <Alert
              type="success"
              showIcon
              message={t("admin.no_error", "Aucune erreur. La synchronisation s'est terminée avec succès.")}
            />
          )}
        </div>

        {/* Raw JSON collapse */}
        <div style={{ marginTop: "16px" }}>
          <Collapse
            size="small"
            items={[
              {
                key: "raw-json",
                label: (
                  <span>
                    <CodeOutlined style={{ marginRight: 6 }} />
                    {t("admin.modal.raw_json", "Données brutes (JSON)")}
                  </span>
                ),
                children: <JsonPre>{JSON.stringify(item, null, 2)}</JsonPre>,
              },
            ]}
          />
        </div>
      </div>
    </Modal>
  );
};

export default AdminBatchDetailModal;
